import { useState } from "react";
import { Printer, Target } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { competenciaAtual, nomeDoMes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { useIsMobile } from "@/lib/use-media-query";
import { cn } from "@/lib/cn";
import { reais } from "@/lib/dinheiro";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { moldura } from "../estilos";
import { TabelaDoDre, type ColunaDoDre } from "./TabelaDoDre";
import { ListaDoDre } from "./ListaDoDre";
import { ConfiancaDoMes } from "./ConfiancaDoMes";
import { ForaDoResultado } from "./ForaDoResultado";
import { categoriasDaLinha, linhasVisiveis, somarDres, valorDaCategoria } from "./linhas-do-dre";
import { dreDasMetas } from "./metas-do-dre";
import { MetasDialog } from "./MetasDialog";
import { PonteDoCaixa } from "./PonteDoCaixa";
import { RentabilidadePorCliente } from "./RentabilidadePorCliente";
import { DreImpresso } from "./DreImpresso";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const mesCurto = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(2, 4)}`;
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type Modo = "mes" | "ano";
type Comparacao = "anterior" | "anoPassado" | "meta";

function Alternador<T extends string>({ valor, opcoes, onChange, rotulo }: { valor: T; opcoes: { valor: T; rotulo: string }[]; onChange: (v: T) => void; rotulo: string }) {
  return (
    <div role="radiogroup" aria-label={rotulo} className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
      {opcoes.map((o) => (
        <button key={o.valor} type="button" role="radio" aria-checked={valor === o.valor} onClick={() => onChange(o.valor)} className={cn("rounded px-2.5 py-1 text-xs font-medium", valor === o.valor ? "bg-brand-600 text-white" : "text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted")}>
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

export function AbaDoDre({ competencia, onConfigurar }: { competencia: string; onConfigurar: () => void }) {
  const [modo, setModo] = useState<Modo>("mes");
  const [comparar, setComparar] = useState<Comparacao>("anterior");
  const celular = useIsMobile();
  const pode = usePermissao("finance.manage");
  const [metasAbertas, setMetasAbertas] = useState(false);
  const q = trpc.financeiro.dre.periodo.useQuery({ competencia, modo }, { placeholderData: keepPreviousData });
  const dados = q.data;
  if (!dados) return <div className="h-64 animate-pulse rounded-xl bg-zinc-100 dark:bg-muted" />;

  const atual = dados.meses.find((m) => m.competencia === competencia) ?? dados.meses[dados.meses.length - 1]!;
  let colunas: ColunaDoDre[];
  if (dados.modo === "mes") {
    const outro = comparar === "anterior" ? dados.meses[1] : comparar === "anoPassado" ? dados.meses[2] : null;
    colunas = [
      ...(comparar === "meta" ? [{ chave: "meta", rotulo: "Meta", dre: dreDasMetas(dados.metas ?? {}) }] : []),
      ...(outro ? [{ chave: outro.competencia, rotulo: mesCurto(outro.competencia), dre: outro.dre }] : []),
      { chave: atual.competencia, rotulo: mesCurto(atual.competencia), dre: atual.dre, comPercentual: true },
    ];
  } else {
    const total = somarDres(dados.meses.map((m) => m.dre));
    colunas = [...dados.meses.map((m) => ({ chave: m.competencia, rotulo: mesCurto(m.competencia), dre: m.dre })), { chave: "total", rotulo: `Total ${competencia.slice(0, 4)}`, dre: total, comPercentual: true }];
  }
  const principal = colunas[colunas.length - 1]!;
  const comparacaoNoCelular = dados.modo === "mes" ? (colunas.length > 1 ? colunas[0]! : null) : { ...colunas.find((c) => c.chave === competencia)!, rotulo: mesCurto(competencia) };

  const linhasDoArquivo = linhasVisiveis(colunas.map((c) => c.dre)).flatMap((l) => [
    { rotulo: l.rotulo, valores: colunas.map((c) => l.valor(c.dre)) },
    ...categoriasDaLinha(l, colunas.map((c) => c.dre)).map((cat) => ({ rotulo: `    ${cat.nome}`, valores: colunas.map((c) => valorDaCategoria(l, c.dre, cat.id)) })),
  ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Alternador rotulo="Período" valor={modo} onChange={setModo} opcoes={[{ valor: "mes", rotulo: "Mês" }, { valor: "ano", rotulo: "Ano" }]} />
        {modo === "mes" && (
          <Alternador rotulo="Comparar com" valor={comparar} onChange={setComparar} opcoes={[{ valor: "anterior", rotulo: "Mês anterior" }, { valor: "anoPassado", rotulo: "Ano passado" }, { valor: "meta", rotulo: "Meta" }]} />
        )}
        {modo === "mes" && pode && (
          <button type="button" onClick={() => setMetasAbertas(true)} className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10">
            <Target className="h-3.5 w-3.5" /> Metas do mês
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
          >
            <Printer className="h-4 w-4" /> Baixar PDF
          </button>
          <BotaoExportar
            nome={`dre-${dados.modo === "ano" ? competencia.slice(0, 4) : competencia}`}
            tela="dre"
            titulo="DRE"
            planilha="dre"
            quantidade={linhasDoArquivo.length}
            idDe={(l) => l.rotulo}
            buscarLinhas={async () => linhasDoArquivo}
            colunas={[{ titulo: "Linha", valor: (l) => l.rotulo }, ...colunas.map((c, i) => ({ titulo: c.rotulo, valor: (l: (typeof linhasDoArquivo)[number]) => l.valores[i]! / 100 }))]}
          />
        </div>
      </div>

      <ConfiancaDoMes confianca={dados.confianca} config={dados.config} mesAtual={competencia >= competenciaAtual()} onConfigurar={onConfigurar} />
      {(atual.semFoto || atual.mudouDepois) && (
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">
          {atual.semFoto ? `${capital(nomeDoMes(competencia))} foi fechado antes do DRE existir: estes são os números de hoje.` : "Este mês mudou depois de fechado: a tabela mostra o número oficial do fechamento."}
        </p>
      )}

      <section className={cn(moldura, "overflow-hidden")}>
        {celular ? <ListaDoDre principal={principal} comparacao={comparacaoNoCelular} /> : <TabelaDoDre colunas={colunas} variacao={dados.modo === "mes" && colunas.length > 1 ? [0, 1] : undefined} rotuloDaVariacao={comparar === "meta" && dados.modo === "mes" ? "vs. meta" : undefined} />}
      </section>

      {dados.saldoDaProvisaoCents != null && (
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">
          Provisão de 13º e férias ainda não paga até {nomeDoMes(competencia)}: <strong className="tabular-nums text-zinc-900 dark:text-foreground">{reais(dados.saldoDaProvisaoCents)}</strong>. É o que guardar para quando o 13º e as férias vencerem.
        </p>
      )}

      <ForaDoResultado dre={dados.modo === "mes" ? atual.dre : principal.dre} />
      <PonteDoCaixa ponte={dados.ponte} />
      <DreImpresso
        colunas={colunas}
        periodo={dados.modo === "ano" ? `Janeiro a ${nomeDoMes(competencia)}` : capital(nomeDoMes(competencia))}
        fora={(dados.modo === "mes" ? atual.dre : principal.dre).foraDoResultado}
      />
      <RentabilidadePorCliente clientes={dados.rentabilidade} materialRateado={dados.config.custoDoMaterial === "COMPRAS"} />
      {dados.modo === "mes" && (
        <MetasDialog aberto={metasAbertas} onFechar={() => setMetasAbertas(false)} competencia={competencia} metas={dados.metas ?? {}} anoPassado={dados.meses[2]?.dre ?? null} />
      )}
    </div>
  );
}
